import React, { useEffect, useMemo, useState } from 'react';
import "./style/bilan.css";
import { toast } from "react-toastify";
import { useGetBilanComptableQuery } from 'actions/comptabilite/queries';
import { useCorrigerBilanComptableMutation } from 'actions/comptabilite/mutations';
import { useDispatch } from 'react-redux';
import { ApiMananger } from 'actions/rtkquery';
import {
  exportBilanComptableExcel,
  exportBilanComptablePdf,
} from './utils/exportComptaExcel';
import {
  exportButtonGroupStyle,
  exportExcelButtonStyle,
  exportPdfButtonStyle,
} from './utils/exportButtonStyles';
import TableLoader from 'components/Loader/TableLoader';

const Bilan = ({ dossier_id, date_debut, date_fin }) => {
  const dispatch = useDispatch();
  const {
    data,
    isLoading,
    refetch,
  } = useGetBilanComptableQuery({ dossier_id, date_debut, date_fin }, { skip: !dossier_id });
  const [corrigerBilan, { isLoading: correcting }] = useCorrigerBilanComptableMutation();

  useEffect(() => {
    if (dossier_id) refetch();
  }, [dossier_id, date_debut, date_fin, refetch]);

  // ── Formatage ──────────────────────────────────────────────────
  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '-';
    const n = parseFloat(v);
    if (isNaN(n)) return '-';
    if (n === 0) return '-';
    return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const actif  = data?.actif  || [];
  const passif = data?.passif || [];
  const diagnostic = data?.diagnostic_desequilibre;
  const suggestion = diagnostic?.suggestion_regularisation?.ecriture_suggeree;
  const [correctionForm, setCorrectionForm] = useState({
    motif: "Regularisation bilan",
    date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    if (suggestion) {
      setCorrectionForm((prev) => ({
        ...prev,
        code_debit: String(suggestion.code_debit || ""),
        code_credit: String(suggestion.code_credit || ""),
        montant: String(suggestion.montant || ""),
      }));
    }
  }, [suggestion]);

  const comptesNonAffectes = useMemo(
    () => diagnostic?.comptes_non_affectes_top || [],
    [diagnostic],
  );

  // ── États UI (après tous les hooks) ───────────────────────────
  if (!dossier_id) return (
    <div className="bilan-container">
      <p style={{ color: 'red', textAlign: 'center' }}>dossier_id manquant</p>
    </div>
  );

  if (isLoading) return (
    <div className="bilan-container">
      <div style={{ background: '#fff', borderRadius: '12px', padding: '30px', border: '1px solid #c8d8db', margin: '20px 0' }}>
        <TableLoader
          message="Établissement du bilan comptable..."
          subMessage="Calcul des masses Actif / Passif et vérification de l'équilibre"
          minHeight="350px"
        />
      </div>
    </div>
  );

  if (!data) return (
    <div className="bilan-container">
      <p style={{ textAlign: 'center', color: '#9CA3AF' }}>Aucune donnée disponible.</p>
    </div>
  );
  const handleExportExcel = () => {
    exportBilanComptableExcel({
      actif,
      passif,
      totalActif: data.total_actif || 0,
      totalPassif: data.total_passif || 0,
      equilibre: !!data.equilibre,
      resultatNet: data.resultat_net || 0,
      dossierId: dossier_id,
    });
  };
  const handleExportPdf = () => {
    exportBilanComptablePdf({
      actif,
      passif,
      totalActif: data.total_actif || 0,
      totalPassif: data.total_passif || 0,
      equilibre: !!data.equilibre,
      resultatNet: data.resultat_net || 0,
      dossierId: dossier_id,
    });
  };
  const handleCorrectionChange = (field) => (e) => {
    setCorrectionForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCorrigerBilan = async (e) => {
    e.preventDefault();
    
    try {
      const res = await corrigerBilan({
        dossier_id,
        motif: correctionForm.motif || "Régularisation automatique",
        date: correctionForm.date,
      }).unwrap();
      
      toast.success(res?.message || "Régularisation enregistrée avec succès.");
      
      // Invalider manuellement tous les caches comptables
      dispatch(ApiMananger.util.invalidateTags(['INVALIDATE_CLIENT']));
      
      // Attendre un peu puis forcer le rechargement
      setTimeout(() => {
        refetch();
        window.dispatchEvent(new Event("compta:refresh"));
      }, 300);
    } catch (error) {
      toast.error(error?.data?.message || "La correction du bilan a échoué.");
    }
  };

  // Séparer le grand-total des autres lignes pour l'afficher en dernier
  const actifLignes  = actif.filter(l => l.type !== 'grand-total');
  const passifLignes = passif.filter(l => l.type !== 'grand-total');
  const actifTotal   = actif.find(l => l.type === 'grand-total');
  const passifTotal  = passif.find(l => l.type === 'grand-total');

  const maxRows = Math.max(actifLignes.length, passifLignes.length);

  // ── Classe CSS selon le type de ligne ─────────────────────────
  const rowClass = (item) => {
    if (!item) return '';
    if (item.type === 'grand-total') return 'grand-total-row';
    if (item.type === 'total')       return 'total-row';
    if (item.type === 'section')     return 'section-row';
    return '';
  };

  return (
    <div className="bilan-container">

      {/* Titre + indicateur d'équilibre */}
      <div className="compta-header-row" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <h2 className="compta-title-wrap" style={{ margin: 0, color: '#2c5f2d' }}>Bilan</h2>
        <div style={exportButtonGroupStyle} className="compta-export-group">
          <button type="button" onClick={handleExportExcel} style={exportExcelButtonStyle}>
            Export Excel
          </button>
          <button type="button" onClick={handleExportPdf} style={exportPdfButtonStyle}>
            Export PDF
          </button>
        </div>
        <span className="compta-status-pill" style={{
          fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20,
          backgroundColor: data.equilibre ? '#D1FAE5' : '#FEE2E2',
          color:           data.equilibre ? '#065F46' : '#DC2626',
          border: `1px solid ${data.equilibre ? '#6EE7B7' : '#FCA5A5'}`,
        }}>
          {data.equilibre ? '✓ Bilan équilibré' : '✗ Bilan déséquilibré'}
        </span>
        {!data.equilibre && (
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>
            Actif : {fmt(data.total_actif)} | Passif : {fmt(data.total_passif)}
          </span>
        )}
      </div>

      {!data.equilibre && (
        <div style={{
          border: '1px solid #fecaca',
          background: '#fff7ed',
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          width: "100%",
          overflowX: "hidden",
        }}>
          <h4 style={{ marginTop: 0, marginBottom: 8, color: '#b91c1c' }}>
            ⚠️ Bilan Déséquilibré
          </h4>
          <p style={{ margin: "0 0 8px 0", fontSize: 13 }}>
            Écart constaté : <strong>{diagnostic?.ecart_absolu?.toLocaleString("fr-FR")} FCFA</strong>
            {" "}({diagnostic?.ecart > 0 ? "Actif supérieur au passif" : "Passif supérieur à l'actif"}).
          </p>
          <p style={{ margin: "0 0 12px 0", fontSize: 12, color: "#7f1d1d" }}>
            Cliquez sur le bouton ci-dessous pour corriger automatiquement le déséquilibre.
            Une écriture de régularisation sera créée avec les comptes appropriés.
          </p>

          {comptesNonAffectes.length > 0 && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#7f1d1d', marginBottom: 6 }}>
                📋 Comptes non affectés (causes probables)
              </summary>
              <ul style={{ margin: "6px 0 0 18px", fontSize: 12 }}>
                {comptesNonAffectes.slice(0, 6).map((item) => (
                  <li key={item.compte}>
                    {item.compte} ({item.orientation}) : {Number(item.solde_net).toLocaleString("fr-FR")} FCFA
                    {item.libelle && ` - ${item.libelle}`}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <form
            onSubmit={handleCorrigerBilan}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              width: "100%",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label htmlFor="bilan-correction-date" style={{ fontSize: 12, fontWeight: 600, color: "#7f1d1d" }}>
                Date de l&apos;écriture de régularisation
              </label>
              <input
                id="bilan-correction-date"
                type="date"
                value={correctionForm.date}
                onChange={handleCorrectionChange("date")}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
                aria-label="Date de l'écriture comptable"
              />
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label htmlFor="bilan-correction-motif" style={{ fontSize: 12, fontWeight: 600, color: "#7f1d1d" }}>
                Motif de la régularisation (optionnel)
              </label>
              <input
                id="bilan-correction-motif"
                type="text"
                placeholder="Ex: Régularisation écart bilan"
                value={correctionForm.motif}
                onChange={handleCorrectionChange("motif")}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
                aria-label="Libellé ou motif de la régularisation"
              />
            </div>
            
            <button
              type="submit"
              disabled={correcting}
              style={{
                padding: '10px 16px',
                border: "none",
                borderRadius: 6,
                background: correcting ? "#9ca3af" : "#b91c1c",
                color: "#fff",
                fontWeight: 600,
                fontSize: '14px',
                cursor: correcting ? "not-allowed" : "pointer",
                transition: 'background 0.2s',
              }}
            >
              {correcting ? "⏳ Correction en cours..." : "✓ Corriger automatiquement le bilan"}
            </button>
            
            <p style={{ margin: 0, fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>
              💡 La correction sera effectuée automatiquement avec les comptes SYSCOHADA appropriés
              ({diagnostic?.ecart > 0 ? "89 → 7591 (produit provisionné)" : "6591 → 89 (charge provisionnée)"}).
            </p>
          </form>
        </div>
      )}

      <div className="bilan-table-wrapper">
        <table className="bilan-table">
          <thead>
            <tr className="header-row-main">
              <th className="col-ref">REF</th>
              <th className="col-label">ACTIF</th>
              <th className="col-net-n1">BRUT</th>
              <th className="col-net-n1">AMORT</th>
              <th className="col-net-n1">NET</th>
              <th className="col-ref-passif">REF</th>
              <th className="col-label-passif">PASSIF</th>
              <th className="col-brut-p">NET</th>
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: maxRows }).map((_, i) => {
              const a = actifLignes[i];
              const p = passifLignes[i];

              // La classe de la ligne est dictée par le type de l'élément le plus "lourd"
              const rc = rowClass(a?.type === 'total' || a?.type === 'section' ? a
                       : p?.type === 'total' || p?.type === 'section' ? p : null);

              return (
                <tr key={i} className={rc}>
                  {/* ── ACTIF ── */}
                  {a ? (
                    <>
                      <td className={`col-ref ${a.type === 'total' ? 'total-cell' : ''}`}>
                        {a.ref}
                      </td>
                      <td className={`col-label ${a.type !== 'item' ? 'section-label' : ''} ${a.type === 'total' ? 'total-cell' : ''}`}>
                        {a.label}
                      </td>
                      <td className={`col-net-n1 ${a.type === 'total' ? 'total-cell' : ''}`}>
                        {fmt(a.brut)}
                      </td>
                      <td className={`col-net-n1 ${a.type === 'total' ? 'total-cell' : ''}`} style={{ color: '#EF4444' }}>
                        {fmt(a.amortissement)}
                      </td>
                      <td className={`col-net-n1 ${a.type === 'total' ? 'total-cell' : ''}`}>
                        {fmt(a.net)}
                      </td>
                    </>
                  ) : (
                    <><td /><td /><td /><td /><td /></>
                  )}

                  {/* ── PASSIF ── */}
                  {p ? (
                    <>
                      <td className={`col-ref-passif ${p.type === 'total' ? 'total-cell' : ''}`}>
                        {p.ref}
                      </td>
                      <td className={`col-label-passif ${p.type !== 'item' ? 'section-label' : ''} ${p.type === 'total' ? 'total-cell' : ''}`}>
                        {p.negative && <span style={{ marginRight: 4, color: '#EF4444' }}>(-)</span>}
                        {p.sign     && <span style={{ marginRight: 4 }}>({p.sign})</span>}
                        {p.label}
                      </td>
                      <td className={`col-brut-p ${p.type === 'total' ? 'total-cell' : ''}`}>
                        {fmt(p.net)}
                      </td>
                    </>
                  ) : (
                    <><td /><td /><td /></>
                  )}
                </tr>
              );
            })}

            {/* ── Ligne TOTAL GENERAL ── */}
            <tr className="grand-total-row">
              <td className="col-ref">{actifTotal?.ref}</td>
              <td className="col-label section-label">{actifTotal?.label}</td>
              <td className="col-net-n1">{fmt(actifTotal?.brut)}</td>
              <td className="col-net-n1">{fmt(actifTotal?.amortissement)}</td>
              <td className="col-net-n1">{fmt(actifTotal?.net)}</td>

              <td className="col-ref-passif">{passifTotal?.ref}</td>
              <td className="col-label-passif section-label">{passifTotal?.label}</td>
              <td className="col-brut-p">{fmt(passifTotal?.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Résultat net en bas */}
      {data.resultat_net !== undefined && (
        <div style={{
          marginTop: 16, display: 'flex', justifyContent: 'flex-end',
          alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 13, color: '#6B7280' }}>Résultat net de l&apos;exercice :</span>
          <span style={{
            fontSize: 15, fontWeight: 700,
            color: data.resultat_net >= 0 ? '#065F46' : '#DC2626',
          }}>
            {data.resultat_net >= 0 ? '+' : ''}{fmt(data.resultat_net)} FCFA
          </span>
        </div>
      )}

    </div>
  );
};

export default Bilan;